'use client';

import Image from 'next/image';

import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/libs/tailwind';

type SortOrder = 'asc' | 'desc';

type SortableHeaderProps<T extends string> = {
  column: T;
  label: string;
  currentSortBy?: T;
  currentSortOrder?: SortOrder;
  onSort?: (column: T) => void;
  sortingEnabled?: boolean;
  className?: string;
};

type TableProps = React.HTMLAttributes<HTMLTableElement> & {
  ref?: React.RefObject<HTMLTableElement>;
  infiniteScroll?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  hasMore?: boolean;
};

const Table = ({
  ref: externalRef,
  className,
  infiniteScroll = false,
  onLoadMore,
  loadingMore = false,
  hasMore = false,
  ...props
}: TableProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isNearBottom, setIsNearBottom] = useState(false);

  useEffect(() => {
    if (!infiniteScroll) {
      return;
    }

    const handleScroll = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      // Check if we're near the bottom of the container (within 200px)
      const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
      setIsNearBottom(nearBottom);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      // Initial check
      handleScroll();
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [infiniteScroll]);

  // Trigger load more when near bottom and there's more to load
  useEffect(() => {
    if (infiniteScroll && isNearBottom && hasMore && !loadingMore && onLoadMore) {
      onLoadMore();
    }
  }, [infiniteScroll, isNearBottom, hasMore, loadingMore, onLoadMore]);

  // Extract border-related classes to apply only to the container
  const borderClasses = 'border-0.5 border-solid border-neutral-200 rounded-lg';
  const containerClasses = cn('relative w-full overflow-auto', borderClasses, className);

  // Create table classes without border styles to avoid duplication
  const tableClasses = cn('w-full caption-bottom text-sm');

  return (
    <div
      ref={containerRef}
      className={containerClasses}
    >
      <table
        ref={externalRef}
        className={tableClasses}
        {...props}
      />
      {infiniteScroll && loadingMore && (
        <div className="w-full text-center py-3 bg-gray-50">
          <div className="animate-pulse inline-block h-5 w-24 bg-gray-200 rounded"></div>
        </div>
      )}
    </div>
  );
};
Table.displayName = 'Table';

const TableHeader = ({ ref, className, ...props }: React.HTMLAttributes<HTMLTableSectionElement> & { ref?: React.RefObject<HTMLTableSectionElement> }) => (
  <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
);
TableHeader.displayName = 'TableHeader';

const TableBody = ({ ref, className, ...props }: React.HTMLAttributes<HTMLTableSectionElement> & { ref?: React.RefObject<HTMLTableSectionElement> }) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
);
TableBody.displayName = 'TableBody';

const TableFooter = ({ ref, className, ...props }: React.HTMLAttributes<HTMLTableSectionElement> & { ref?: React.RefObject<HTMLTableSectionElement> }) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t bg-muted/50 font-medium [&>tr]:last:border-b-0',
      className,
    )}
    {...props}
  />
);
TableFooter.displayName = 'TableFooter';

const TableRow = ({ ref, className, colspan, ...props }: React.HTMLAttributes<HTMLTableRowElement> & { ref?: React.RefObject<HTMLTableRowElement>; colspan?: number }) => (
  <tr
    ref={ref}
    className={cn(
      'border-b border-gray-200',
      className,
    )}
    {...props}
  />
);
TableRow.displayName = 'TableRow';

const TableHead = ({ ref, className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement> & { ref?: React.RefObject<HTMLTableCellElement> }) => (
  <th
    ref={ref}
    className={cn(
      'h-10 px-2 text-left text-neutral-50 text-body-sm [&:has([role=checkbox])]:pr-0',
      className,
    )}
    {...props}
  />
);
TableHead.displayName = 'TableHead';

const TableCell = ({ ref, className, colspan, ...props }: React.TdHTMLAttributes<HTMLTableCellElement> & { ref?: React.RefObject<HTMLTableCellElement>; colspan?: number }) => (
  <td
    ref={ref}
    className={cn('p-2 [&:has([role=checkbox])]:pr-0', 'text-body-md', className)}
    colSpan={colspan}
    {...props}
  />
);
TableCell.displayName = 'TableCell';

const TableCaption = ({ ref, className, ...props }: React.HTMLAttributes<HTMLTableCaptionElement> & { ref?: React.RefObject<HTMLTableCaptionElement> }) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-muted-foreground', className)}
    {...props}
  />
);
TableCaption.displayName = 'TableCaption';

/**
 * SortableHeader component for table columns that can be sorted
 */
const SortableHeader = <T extends string>({
  column,
  label,
  currentSortBy,
  currentSortOrder = 'asc',
  onSort,
  sortingEnabled = true,
  className,
}: SortableHeaderProps<T>) => {
  const isActive = currentSortBy === column;

  const handleClick = useCallback(() => {
    if (sortingEnabled && onSort) {
      onSort(column);
    }
  }, [onSort, sortingEnabled]);

  return (
    <TableHead
      className={cn(
        'text-center',
        sortingEnabled && 'cursor-pointer',
        className,
      )}
      onClick={handleClick}
    >
      <div className="flex space-x-1">
        <span>{label}</span>
        {sortingEnabled && isActive ? (
          <span className="ml-1">
            {currentSortOrder === 'asc' ? (
              <Image src="/assets/icons/arrow-up.svg" className="text-neutral-60" alt="Arrow up" width={12} height={12} />
            ) : (
              <Image src="/assets/icons/arrow-up.svg" className="rotate-180 text-neutral-60" alt="Arrow down" width={12} height={12} />
            )}
          </span>
        ) : null}
      </div>
    </TableHead>
  );
};

export {
  SortableHeader,
  type SortableHeaderProps,
  type SortOrder,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
